import { AfterContentInit, AfterViewInit, Directive, ElementRef, EventEmitter, HostListener, Input, Output, OnDestroy, afterNextRender } from '@angular/core';
import { debounceTime, Subject } from 'rxjs';

@Directive({
  selector: '[pplFocusTrap]',
  standalone: true,
})
export class FocusTrapDirective implements AfterContentInit, AfterViewInit, OnDestroy {
  /*** Element Ref should have [tabindex="-1"] to be focusable  */

  @Output() enter = new EventEmitter<KeyboardEvent>();
  @Output() escape = new EventEmitter<KeyboardEvent>();

  /*** Element that will be refocused after leaving focus trap */
  @Input() triggerEl: HTMLElement | null = null;
  private firstFocusableEl: HTMLElement | null = null;
  private lastFocusableEl: HTMLElement | null = null;
  private focusableElements:  HTMLElement[] = [];

  private get noElementsToTrap(): boolean {
    return !this.firstFocusableEl && !this.lastFocusableEl;
  }

  private setFocusableElsSub = new Subject<void>();

  selectors = ['button', 'input', 'select', 'textarea', '[tabindex="0"]']; // Focusable Elements

  constructor(
    private el: ElementRef
  ) {
    afterNextRender({
      read: () => {
        this.setFocusableElsSub.next();
      }
    });

    this.setFocusableElsSub.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.setFocusableElements();
    })
  }

  ngAfterContentInit(): void {
    if (!this.triggerEl) {
      this.triggerEl = document.activeElement as HTMLElement;
    }
    this.setFocusableElements();
  }

  setFocusableElements() {
    const focusableSelectors = this.selectors.join(', ');
    const newfocusableElements = this.el.nativeElement.querySelectorAll(focusableSelectors) as HTMLElement[];
    if (
      newfocusableElements?.length > 0 && 
      newfocusableElements.length !== this.focusableElements.length
    ) {
      this.focusableElements = newfocusableElements;
      this.firstFocusableEl = this.focusableElements[0];
      this.lastFocusableEl = this.focusableElements[this.focusableElements.length - 1];
    } else if (newfocusableElements.length === 0) {
      this.focusableElements = newfocusableElements;
      // Creates trap, but will exit on TAB
      this.el.nativeElement.tabIndex = -1;
      this.el.nativeElement.focus();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.firstFocusableEl?.focus(), 200)
  }

  @HostListener('keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    const { key, shiftKey, code } = event;

    if (key === 'Tab') {
      if (this.noElementsToTrap) {
        this.escape.emit(event);
        return;
      }
      if (shiftKey && document.activeElement === this.firstFocusableEl) {
        // Cycles back to last element
        event.preventDefault();
        this.lastFocusableEl?.focus();
      } else if (!shiftKey && document.activeElement === this.lastFocusableEl) {
        // Cycles back to first element
        event.preventDefault();
        this.firstFocusableEl?.focus();
      }
    } else if (key === 'Escape') {
      this.triggerEl?.focus();
      this.triggerEl = null;
      this.escape.emit(event);
    } else if (key === 'Enter' || code === 'Space') {
      this.enter.emit(event);
    }
  }

  ngOnDestroy() {
    this.triggerEl?.focus();
  }
}
